import { useEffect, useRef } from "react";
import { FieldValues, UseFormHandleSubmit, UseFormWatch } from "react-hook-form";

interface UseCarePlanAutoSaveProps<T extends FieldValues> {
  enabled: boolean;
  baselineData: T;
  watch: UseFormWatch<T>;
  handleSubmit: UseFormHandleSubmit<T>;
  onSubmit: (data: T) => void | Promise<void>;
  delayMs?: number;
}

export const useCarePlanAutoSave = <T extends FieldValues>({
  enabled,
  baselineData,
  watch,
  handleSubmit,
  onSubmit,
  delayMs = 3000,
}: UseCarePlanAutoSaveProps<T>) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedSnapshotRef = useRef<string>(JSON.stringify(baselineData));
  const lastChangedFieldRef = useRef<string | undefined>(undefined);
  const onSubmitRef = useRef(onSubmit);

  const dispatchAutoSaveStatus = (state: "saving" | "saved", fieldName?: string) => {
    window.dispatchEvent(new CustomEvent("careplan-autosave-status", {
      detail: {
        state,
        fieldName,
        timestamp: new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }),
      },
    }));
  };

  useEffect(() => {
    lastSavedSnapshotRef.current = JSON.stringify(baselineData);
  }, [baselineData]);

  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  useEffect(() => {
    if (!enabled) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    const subscription = watch((value, info) => {
      const nextSnapshot = JSON.stringify(value);
      if (nextSnapshot === lastSavedSnapshotRef.current) {
        return;
      }

      lastChangedFieldRef.current = info.name;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        const latestData = watch() as T;
        const submittedSnapshot = JSON.stringify(latestData);
        if (submittedSnapshot === lastSavedSnapshotRef.current) {
          return;
        }

        dispatchAutoSaveStatus("saving", lastChangedFieldRef.current);
        void Promise.resolve(onSubmitRef.current(latestData))
          .then(() => {
            lastSavedSnapshotRef.current = submittedSnapshot;
            dispatchAutoSaveStatus("saved", lastChangedFieldRef.current);
          })
          .catch(() => {
            dispatchAutoSaveStatus("saved", lastChangedFieldRef.current);
          });
      }, delayMs);
    });

    return () => {
      subscription.unsubscribe();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [delayMs, enabled, handleSubmit, watch]);
};
